import React, { useEffect, useState, useRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import useEventListener from '../hooks/useEventListener';

import { path } from 'ramda';

const EditBox = ({
    start: initStart,
    end: initEnd,
    getCursorPosition,
    update,
    imageUrl,
    remove,
    width,
    height,
}) => {
    const [start, setStart] = useState(initStart);
    const [end, setEnd] = useState(initEnd);
    const [direction, setDirection] = useState('');
    const [isDragging, setDrag] = useState(false);

    const boxRef = useRef(null);
    const ownerDocument =
        boxRef && boxRef.current ? boxRef.current.ownerDocument : window;

    const onMouseDown = direction => () => {
        if (boxRef.current) {
            setDirection(direction);
            setDrag(true);
        }
    };

    const _handleDelete = e => {
        if (e.keyCode === 8) {
            remove();
        }
    };

    useEffect(() => {
        if (!imageUrl) return;
        document.addEventListener('keydown', _handleDelete);
        return () => {
            document.removeEventListener('keydown', _handleDelete);
        };
    }, []);

    const onMouseMove = useCallback(
        e => {
            if (!imageUrl) return;
            if (!isDragging) return;
            const [x, y] = getCursorPosition(e);
            const [startX, startY] = start;
            const [endX, endY] = end;

            const offsetX = startX - x;
            const offsetY = startY - y;

            let newStart = start;
            let newEnd = end;

            switch (direction) {
                case 'c': {
                    const halfW = endX / 2;
                    const halfH = endY / 2;
                    newStart = [x - halfW, y - halfH];
                    break;
                }
                case 'br':
                    newEnd = [x - startX, y - startY];
                    break;

                case 'bl':
                    newStart = [x, startY];
                    newEnd = [endX + offsetX, y - startY];
                    break;

                case 'tr':
                    newStart = [startX, y];
                    newEnd = [x - startX, endY + offsetY];
                    break;

                case 'tl':
                    newStart = [x, y];
                    newEnd = [endX + offsetX, endY + offsetY];
                    break;

                case 'cr':
                    newEnd = [x - startX, endY];
                    break;

                case 'cl':
                    newStart = [x, startY];
                    newEnd = [endX + offsetX, endY];
                    break;

                case 'bc':
                    newEnd = [endX, y - startY];
                    break;

                case 'tc':
                    newStart = [startX, y];
                    newEnd = [endX, endY + offsetY];
                    break;
            }

            setStart(newStart);
            setEnd(newEnd);
        },
        [imageUrl, isDragging, getCursorPosition, start, end, direction],
    );

    const onMouseUp = async () => {
        if (!imageUrl) return;
        if (!isDragging || !boxRef.current) return;

        const { ownerDocument } = boxRef.current;
        ownerDocument.removeEventListener('mousemove', onMouseMove);
        ownerDocument.removeEventListener('mouseup', onMouseUp);
        await update({ start: start, end: end });
        setDrag(false);
    };

    useEventListener('mousemove', onMouseMove, ownerDocument);
    useEventListener('mouseup', onMouseUp, ownerDocument);

    const style = {
        left: path(['0'], start),
        top: path(['1'], start),
        width: path(['0'], end),
        height: path(['1'], end),
    };

    const imageCoors = {
        left: -style.left,
        top: -style.top,
        width: `${width}px`,
        height: `${height}px`,
    };

    if (!imageUrl) return null;

    return (
        <div
            className="gds-lens gds-lens--active gds-lens--valid"
            style={style}
        >
            <div className="gds-lens-update-container" ref={boxRef}>
                <div
                    onMouseDown={onMouseDown('c')}
                    className="gds-lens__viewport --box-viewport -cursor-events--none"
                >
                    <img
                        className="gds-lens__image image-annotation"
                        style={imageCoors}
                        src={imageUrl}
                    />
                </div>
                <div
                    onMouseDown={onMouseDown('tl')}
                    className="gds-lens__handle --box-handle gds-lens__handle--t-l"
                />
                <div
                    onMouseDown={onMouseDown('tc')}
                    className="gds-lens__handle --box-handle gds-lens__handle--t-c"
                />
                <div
                    onMouseDown={onMouseDown('tr')}
                    className="gds-lens__handle --box-handle gds-lens__handle--t-r"
                />
                <div
                    onMouseDown={onMouseDown('cl')}
                    className="gds-lens__handle --box-handle gds-lens__handle--c-l"
                />
                <div
                    onMouseDown={onMouseDown('cr')}
                    className="gds-lens__handle --box-handle gds-lens__handle--c-r"
                />
                <div
                    onMouseDown={onMouseDown('bl')}
                    className="gds-lens__handle --box-handle gds-lens__handle--b-l"
                />
                <div
                    onMouseDown={onMouseDown('bc')}
                    className="gds-lens__handle --box-handle gds-lens__handle--b-c"
                />
                <div
                    onMouseDown={onMouseDown('br')}
                    className="gds-lens__handle --box-handle gds-lens__handle--b-r"
                />
                <div className="gds-lens__guide-grid gds-lens__guide-grid--h" />
                <div className="gds-lens__guide-grid gds-lens__guide-grid--v" />
            </div>
            <div className="gds-lens__btn-cluster">
                <button
                    type="button"
                    onClick={remove}
                    className="gds-circular-button gds-circular-button--xs gds-circular-button--danger"
                >
                    <i className="fa fa-fw fa-trash-alt" />
                </button>
            </div>
        </div>
    );
};

EditBox.propTypes = {
    start: PropTypes.array.isRequired,
    end: PropTypes.array.isRequired,
    imageUrl: PropTypes.string.isRequired,
    getCursorPosition: PropTypes.func.isRequired,
    update: PropTypes.func,
    remove: PropTypes.func,
    width: PropTypes.number,
    height: PropTypes.number,
};

export default EditBox;
